import { useEffect, useRef } from "react";
import { Eraser } from "lucide-react";
import { Button } from "@wa/ui";

/**
 * A signature drawn with a mouse or a finger. The result is a PNG data URL.
 *
 * The pad is white with dark ink in BOTH themes, on purpose. It is not chrome:
 * what is drawn here is exported as an image and printed onto the contract's
 * PDF, which is a white page. A pad that followed dark mode would hand the
 * reader a canvas whose strokes look right on screen and vanish on paper.
 */
export function SignaturePad({
  value,
  onChange,
  label,
  hint,
  clearLabel,
}: {
  value: string | null;
  onChange: (dataUrl: string | null) => void;
  label: string;
  hint: string;
  clearLabel: string;
}) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const ctxRef = useRef<CanvasRenderingContext2D | null>(null);
  const sizeRef = useRef({ w: 0, h: 0 });
  const drawing = useRef(false);
  const inked = useRef(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    // Layout dimensions (clientWidth/Height), so the backing store is sharp at
    // the size the pad is actually drawn.
    const w = canvas.clientWidth || 320;
    const h = canvas.clientHeight || 128;
    const ratio = window.devicePixelRatio || 1;
    sizeRef.current = { w, h };
    canvas.width = Math.round(w * ratio);
    canvas.height = Math.round(h * ratio);
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.scale(ratio, ratio);
    ctx.lineWidth = 2.2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#0f172a";
    ctxRef.current = ctx;
  }, []);

  function point(e: React.PointerEvent<HTMLCanvasElement>) {
    const rect = e.currentTarget.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  }
  function down(e: React.PointerEvent<HTMLCanvasElement>) {
    const ctx = ctxRef.current;
    if (!ctx) return;
    drawing.current = true;
    e.currentTarget.setPointerCapture(e.pointerId);
    const { x, y } = point(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
  }
  function move(e: React.PointerEvent<HTMLCanvasElement>) {
    if (!drawing.current) return;
    const ctx = ctxRef.current;
    if (!ctx) return;
    const { x, y } = point(e);
    ctx.lineTo(x, y);
    ctx.stroke();
    inked.current = true;
  }
  function up() {
    if (!drawing.current) return;
    drawing.current = false;
    const canvas = canvasRef.current;
    if (inked.current && canvas) onChange(canvas.toDataURL("image/png"));
  }
  function clear() {
    const ctx = ctxRef.current;
    if (ctx) ctx.clearRect(0, 0, sizeRef.current.w, sizeRef.current.h);
    inked.current = false;
    onChange(null);
  }

  return (
    <div>
      <div className="flex min-h-8 items-center justify-between gap-2">
        <span className="text-xs font-medium text-muted-foreground">{label}</span>
        {value && (
          <Button variant="ghost" size="sm" onClick={clear} className="-mr-2 text-muted-foreground">
            <Eraser />
            {clearLabel}
          </Button>
        )}
      </div>
      <div className="mt-1 overflow-hidden rounded-lg border border-border bg-white">
        <canvas
          ref={canvasRef}
          aria-label={label}
          className="block h-32 w-full cursor-crosshair touch-none"
          onPointerDown={down}
          onPointerMove={move}
          onPointerUp={up}
          onPointerLeave={up}
        />
      </div>
      {!value && <p className="mt-1.5 text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}
